/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Dialog, Pagination, Search, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import LearnerTableSkeletonRows from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/LearnerTableSkeletonRows';
import { learnerTableStyles } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/learnerTableStyles';
import type { Learner } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { useAudienceLearnerList } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearnerList';
import type { LearnerListAudience } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import useLearnerList, { LEARNER_LIST_PAGE_SIZE } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';
import { useMessagingTrackingData } from 'bundles/enterprise-admin-messaging-v2/hooks/useMessagingTrackingData';
import {
  MESSAGING_BUTTON,
  MESSAGING_MODAL,
  MESSAGING_TEXT_INPUT,
} from 'bundles/enterprise-admin-messaging-v2/utils/eventing';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  learners: Learner[];
  /**
   * When provided (any filter-backed entry point), the cohort is fetched
   * server-side from this ready `{ orgId, filter }` audience instead of
   * rendering the in-memory `learners`. Absent for the manual flow.
   */
  audience?: LearnerListAudience;
  onEdit: () => void;
  onBack: () => void;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  searchRow: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
  `,
  searchInput: css`
    flex: 1;

    /* CDS Search has an intrinsic width and doesn't expand to fill its
       flex parent on its own. Force both the outer wrapper and the inner
       MUI input root (which carries a min-width: 240px) to stretch so the
       search bar sits flush with the Edit button per design. */
    .cds-Search-base,
    .cds-Search-inputRoot {
      width: 100%;
    }
  `,
  loadError: css`
    /* Always-mounted live region: keep it in the DOM for screen readers and
       collapse it (no flex gap) until an error is actually showing. */
    :empty {
      display: none;
    }
  `,
};

/**
 * Read-only view of the current learner cohort. Search filters the
 * displayed list client-side; "Edit learner list" hands off to the editor
 * sub-screen.
 */
const LearnerListScreen = ({ learners, audience, onEdit, onBack }: Props) => {
  // Both hooks are called unconditionally (Rules of Hooks); the server hook
  // skips its query when there's no audience, so the inactive one is cheap.
  const clientList = useLearnerList({ list: learners });
  const serverList = useAudienceLearnerList({ audience });
  const { searchQuery, setSearchQuery, page, setPage, pageCount, pageRows, filteredCount, loading, error } = audience
    ? serverList
    : { ...clientList, loading: false, error: false };

  const track = useTracker();
  const trackingData = useMessagingTrackingData();

  // Impression: fires once when the read-only learner list appears.
  React.useEffect(() => {
    track('view_modal', { ...trackingData, modal: { name: MESSAGING_MODAL.LEARNER_LIST } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Track the "Edit learner list" click, then run the existing handler. */
  const handleEdit = React.useCallback<() => void>(() => {
    track('click_button', {
      button: { name: MESSAGING_BUTTON.EDIT_LEARNER_LIST },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.LEARNER_LIST },
    });
    onEdit();
  }, [track, trackingData, onEdit]);

  /** Fire the text-input focus event for the learner search field. */
  const handleSearchFocus = React.useCallback<() => void>(() => {
    track('click_text_input', {
      textInput: { name: MESSAGING_TEXT_INPUT.LEARNER_SEARCH },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.LEARNER_LIST },
    });
  }, [track, trackingData]);

  return (
    <React.Fragment>
      <Dialog.Content>
        <div css={styles.body}>
          <div css={styles.searchRow}>
            <div css={styles.searchInput}>
              <Search
                aria-label={_t('Search learners')}
                value={searchQuery}
                onChange={setSearchQuery}
                onFocus={handleSearchFocus}
                placeholder={_t('Search learners')}
                hideSearchButton
                data-testid="ai-message-modal-learner-list-search"
              />
            </div>
            <Button variant="secondary" onClick={handleEdit} data-testid="ai-message-modal-learner-list-edit">
              {_t('Edit learner list')}
            </Button>
          </div>

          {/* Always-mounted live region so the error is announced when it appears. */}
          <div role="alert" css={styles.loadError}>
            {error ? (
              <Typography2 component="span" variant="bodySecondary" color="error">
                {_t('Something went wrong loading learners. Please try again.')}
              </Typography2>
            ) : null}
          </div>

          <div css={learnerTableStyles.tableWrapper}>
            <table
              css={learnerTableStyles.table}
              aria-label={_t('Learner list')}
              aria-busy={loading}
              data-testid="ai-message-modal-learner-list-table"
            >
              <thead>
                <tr>
                  <th css={[learnerTableStyles.th, learnerTableStyles.nameColumn]}>
                    <Typography2 component="span" variant="subtitleMedium" color="supportText">
                      {_t('Name')}
                    </Typography2>
                  </th>
                  <th css={learnerTableStyles.th}>
                    <Typography2 component="span" variant="subtitleMedium" color="supportText">
                      {_t('Email')}
                    </Typography2>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && pageRows.length === 0 ? (
                  <LearnerTableSkeletonRows rowCount={LEARNER_LIST_PAGE_SIZE} columnCount={2} />
                ) : !error && pageRows.length === 0 ? (
                  <tr>
                    <td css={learnerTableStyles.emptyRow} colSpan={2}>
                      <Typography2 component="span" variant="bodySecondary">
                        {_t('No learners match your search.')}
                      </Typography2>
                    </td>
                  </tr>
                ) : (
                  pageRows.map(({ learner }) => (
                    <tr key={learner.id} data-testid={`ai-message-modal-learner-row-${learner.id}`}>
                      <td css={learnerTableStyles.td}>
                        <Typography2 component="span" variant="bodyPrimary">
                          {learner.name}
                        </Typography2>
                      </td>
                      <td css={learnerTableStyles.td}>
                        <Typography2 component="span" variant="bodyPrimary">
                          {learner.email}
                        </Typography2>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {pageCount > 1 && (
              <div css={learnerTableStyles.paginationRow}>
                <Pagination
                  total={filteredCount}
                  pageSize={LEARNER_LIST_PAGE_SIZE}
                  page={page}
                  onPageChange={(_event, nextPage) => setPage(nextPage)}
                  hidePageSizeInput
                  aria-label={_t('Learner list pagination')}
                />
              </div>
            )}
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div css={learnerTableStyles.actions}>
          <Button variant="secondary" onClick={onBack} data-testid="ai-message-modal-learner-list-back">
            {_t('Back')}
          </Button>
        </div>
      </Dialog.Actions>
    </React.Fragment>
  );
};

export default LearnerListScreen;
